'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ExportPanelProps {
  timeRange: string;
}

export default function ExportPanel({ timeRange }: ExportPanelProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv' | 'excel'>('pdf');

  const handleExport = () => {
    setIsExporting(true);
    
    // Simulate export process
    setTimeout(() => {
      // Create mock data for export
      const reportData = {
        generatedAt: new Date().toISOString(),
        timeRange: timeRange,
        format: exportFormat,
        metrics: {
          totalShipments: 1247,
          revenue: 487650,
          successRate: 94.5,
          onTimeRate: 92.3,
        },
      };

      // Create downloadable content
      let content = '';
      let filename = '';
      let mimeType = '';

      if (exportFormat === 'csv') {
        content = 'Metric,Value\n';
        content += `Total Shipments,${reportData.metrics.totalShipments}\n`;
        content += `Total Revenue,$${reportData.metrics.revenue}\n`;
        content += `Success Rate,${reportData.metrics.successRate}%\n`;
        content += `On-Time Rate,${reportData.metrics.onTimeRate}%\n`;
        filename = `analytics-report-${timeRange}days.csv`;
        mimeType = 'text/csv';
      } else if (exportFormat === 'excel') {
        // For Excel, we'd use a library like xlsx, but for demo purposes using CSV format
        content = 'Metric,Value\n';
        content += `Total Shipments,${reportData.metrics.totalShipments}\n`;
        content += `Total Revenue,$${reportData.metrics.revenue}\n`;
        content += `Success Rate,${reportData.metrics.successRate}%\n`;
        content += `On-Time Rate,${reportData.metrics.onTimeRate}%\n`;
        filename = `analytics-report-${timeRange}days.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else {
        // PDF format - simplified text representation
        content = `Analytics & Reports\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n`;
        content += `Time Range: Last ${timeRange} days\n\n`;
        content += `KEY METRICS:\n`;
        content += `Total Shipments: ${reportData.metrics.totalShipments}\n`;
        content += `Total Revenue: $${reportData.metrics.revenue.toLocaleString()}\n`;
        content += `Success Rate: ${reportData.metrics.successRate}%\n`;
        content += `On-Time Rate: ${reportData.metrics.onTimeRate}%\n`;
        filename = `analytics-report-${timeRange}days.pdf`;
        mimeType = 'application/pdf';
      }

      // Create and trigger download
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsExporting(false);
    }, 1500);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground">Export Format:</label>
        <select
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'csv' | 'excel')}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="pdf">PDF</option>
          <option value="csv">CSV</option>
          <option value="excel">Excel</option>
        </select>
      </div>

      <button
        onClick={handleExport}
        disabled={isExporting}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <>
            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            Exporting...
          </>
        ) : (
          <>
            <Icon name="ArrowDownTrayIcon" size={16} />
            Export Report
          </>
        )}
      </button>
    </div>
  );
}